<template>
  <v-col class="col-sm-12">
    <app-card-actions
      action-refresh
      @refresh="refreshComments"
    >
      <template slot="title">
        Comments
      </template>

      <template slot="before-actions">
        <chip
          v-if="unreadCommentsCount"
          :value="`${unreadCommentsCount} New Comment${unreadCommentsCount > 1 ? 's' : ''}`"
          :icon="unreadCommentsCount > 1 ? icons.mdiCommentTextMultiple : icons.mdiCommentText"
          color="error"
          class="mr-3"
          small
        ></chip>
      </template>

      <v-data-table
        :headers="headers"
        :items="commentsFiltered"
        item-key="id"
        :page.sync="pagination.page"
        :items-per-page="pagination.itemsPerPage"
        :sort-by.sync="sort.by"
        :sort-desc.sync="sort.desc"
        :loading="loading"
        hide-default-footer
        class="has-pagination col-sm-12 pa-2"
        loading-text="Loading... Please wait..."
        @page-count="pagination.pageCount = $event"
        @click:row="rowClickHandler"
      >
        <!-- status/is_read -->
        <template #item.is_read="{ item }">
          <div class="is-read-cell">
            <v-tooltip bottom :disabled="item.origin !== 'txp'">
              <template #activator="{ on, attrs }">
                <div v-bind="attrs" v-on="on" class="d-inline-block">
                  <checkbox
                    v-model="item.is_read"
                    :class="item.is_read && 'new'"
                    :disabled="loading || item.is_read || item.origin === 'txp'"
                    simple
                    @click.stop="commentStatusChange(item)"
                  ></checkbox>
                </div>
              </template>
              <span>PWA originating comments cannot be marked as read</span>
            </v-tooltip>
          </div>
        </template>

        <!-- commentator -->
        <template #item.created_by_user_id="{ item }">
          <span :class="!item.is_read && 'new'">
            {{ commentator(item.created_by_user_id) }}
          </span>
        </template>

        <!-- origin/source -->
        <template #item.source="{ item }">
          <span :class="!item.is_read && 'new'">
            {{ item.source && $custom.toProperCase(item.source.replace(/_/g, ' ')) }}
          </span>
        </template>

        <!-- timestamp -->
        <template #item.created="{ item }">
          <span :class="!item.is_read && 'new'">
            {{ $date(item.created).format('MM/DD/YY h:mm A') }}
          </span>
        </template>
      </v-data-table>

      <!-- pagination -->
      <pagination
        :page.sync="pagination.page"
        :items-per-page.sync="pagination.itemsPerPage"
        :page-count="pagination.pageCount"
      ></pagination>
      <v-row
        class="pagination justify-space-between mt-5 mx-4 mb-1"
      >
        <text-area
          v-if="commentAdds"
          v-model="commentBody"
          class="col-sm-10"
        >
        </text-area>
        <btn
          v-if="commentAdds"
          :text="false"
          :icon="icons.mdiSend"
          class="col-sm-1 mx-2 mt-4 pr-n12"
          :disabled="!commentBody"
          @click="commentAdd"
        >
        </btn>
      </v-row>
    </app-card-actions>

    <v-dialog
      v-model="modalState"
      width="700"
      persistent
    >
      <v-card>
        <v-card-title
          ref="commentTitle"
          class="pb-0"
        >
          <v-row class="w-full mb-1">
            <v-col cols="12" class="py-1">
              <strong>Patient:</strong>
              <span class="ml-1">
                {{ getPatientName() }}
              </span>
            </v-col>
          </v-row>

          <v-row
            v-if="comment.encounter"
            class="w-full mb-1"
          >
            <v-col cols="12" class="py-1">
              <strong>Visit Date:</strong>
              <span class="ml-1">
                {{ comment.encounter.visit_date ? $date(comment.encounter.visit_date).format('MM/DD/YYYY') : 'MM/DD/YYYY' }}
              </span>
            </v-col>
          </v-row>

          <v-row
            v-if="comment.encounter"
            class="w-full mb-1"
          >
            <v-col cols="12" class="py-1">
              <strong>Facility:</strong>
              <span class="ml-7">
                {{ facilityTitle() }}
              </span>
            </v-col>
          </v-row>
        </v-card-title>

        <NoteSection
          :label="`From: ${commentator(comment.created_by_user_id)} on ${$date(comment.created).format('MM/DD/YY h:mm A')}`"
          class="view-comment"
        >
          <pre>{{ comment.comment }}</pre>
        </NoteSection>

        <v-card-actions>
          <btn
            label="Close"
            color="secondary"
            :icon="icons.mdiClose"
            @click="modalState = false"
          ></btn>

          <v-spacer></v-spacer>

          <btn
            v-if="comment.encounter"
            label="Review Encounter"
            :icon="icons.mdiAccountMultiple"
            @click="viewEncounter(comment)"
          ></btn>

          <!-- No encounter related, should just allow the user to view the patient profile. -->
          <btn
            v-else
            label="View Patient Profile"
            :icon="icons.mdiAccountMultiple"
            @click="viewPatient(comment.patient_id)"
          ></btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-col>
</template>

<script>
import '@/components/patients/primitives'
import syncLogSetup from '@/mixins/SyncEncounters'
import AppCardActions from '@core/components/app-card-actions/AppCardActions.vue'
import {
  mdiAccountMultiple,
  mdiClose,
  mdiCommentText,
  mdiCommentTextMultiple,
  mdiSend,
} from '@mdi/js'
import { mapState, mapGetters } from 'vuex'
import { mapFields } from 'vuex-map-fields'
import { v4 as uuidv4 } from 'uuid'

const initialState = {
  by: 'created',
  desc: true,
}

export default {
  components: { AppCardActions },
  mixins: [syncLogSetup],
  props: {
    // Expects: 'main', 'encounter, 'insuranceAuth'.
    type: {
      type: String,
      default: 'main',
    },
    encounterId: {
      type: String,
      default: null,
    },
    patientId: {
      type: String,
      default: null,
    },
    commentAdds: {
      type: Boolean,
      default: false,
    },
    itemsPerPage: {
      type: Number,
      default: 10,
    },
  },
  data() {
    const refreshComments = hideOverlay => {
      if (this.online) {
        this.$store.dispatch('auth/refresh')
          .catch(error => {
            setTimeout(() => {
              this.$store.dispatch('notify', { value: error , color: 'error' })
            }, 1000);
          })
        this.$store.dispatch('comments/loadComments')
          .then(response => {
            if (response === true) {
              this.sort = { ...initialState }
              this.$store.dispatch('notify', {
                value: 'Comments updated successfully.', color: 'success',
              })
            } else {
              this.$store.dispatch('notify', { value: `${response}!`, color: 'error' })
            }
          })
          .catch(() => {})
          .finally(() => {
            hideOverlay()
          })
      } else {
        this.$store.dispatch('notify', { value: 'Offline, can\'t download comments!', color: 'error' })
        hideOverlay()
      }
    }

    return {
      refreshComments,
      loading: false,
      sort: { ...initialState },
      modalState: false,
      userId: this.$authUser.userId(),
      comment: {
        encounter_id: null,
        created: null,
        created_by_user_id: null,
        encounter: {
          created_by_user_id: null,
        },
      },
      icons: {
        mdiAccountMultiple,
        mdiCommentTextMultiple,
        mdiCommentText,
        mdiClose,
        mdiSend,
      },
      headers: [
        {
          // Filter out comments not designated for the current provider
          value: 'encounter.created_by_user_id',
          align: 'hide',
        },
        {
          text: 'Read', value: 'is_read', align: 'center', sortable: false,
        },
        { text: 'Commentator', value: 'created_by_user_id' },
        {
          text: this.type === 'main' ? 'Origin' : 'Comment',
          value: this.type === 'main' ? 'source' : 'comment',
        },
        { text: 'Date & Time', value: 'created' },
      ],
      pagination: {
        page: 1,
        pageCount: 0,
        itemsPerPage: this.itemsPerPage,
      },

      // V-models
      commentBody: null,
    }
  },
  computed: {
    ...mapState('comments', { comments: 'items' }),
    ...mapGetters('auth', ['facilities']),
    ...mapFields(['online']),

    // Comments Type
    commentsFiltered() {
      return this.filterComments() || [];
    },
    unreadCommentsCount() {
      if (Array.isArray(this.commentsFiltered) && this.commentsFiltered.length)
        return this.commentsFiltered.filter(x => !x.is_read && x.created_by_user_id !== this.userId).length

      return 0
    },
    newCommentTemplate() {
      return {
        id: uuidv4(),
        encounter_id: this.type === 'insuranceAuth' ? null : this.encounterId,
        comment: this.commentBody,
        source: 'insurance_auth',
        origin: 'txp',
        patient_id: `${this.patientId}`,
        created_by_user_id: this.$authUser.userId(),
        encounter: this.$store.getters['encounters/getById'](this.encounterId),
        is_read: false,
        created: this.$custom.utcNow(),
        sync_log: this.syncLogSetup('I/A Comments Sync: Post Comments'),
      }
    },
    commentEncounter() {
      return this.$store.getters['encounters/getById'](this.comment.encounter_id)
    },
    isNoteSynced() {
      return !!this.newCommentTemplate.encounter.is_synced
    },
    currentFacilitiesIds() {
      return this.facilities.map(f => f.id) || []
    },
  },
  watch: {
    modalState() {
      // Scroll to top of modal
      const modalTitle = this.$refs.commentTitle
      if (modalTitle) {
        modalTitle.scrollIntoView(true)
      }
    },
  },
  mounted() {
  },
  methods: {
    filterComments() {
      switch (this.type) {
        case 'main':
          return this.comments.filter(comment => (comment.created_by_user_id !== this.userId || comment.source === 'face_sheet'))
        case 'insuranceAuth':
          return this.comments.filter(comment => comment.source === 'insurance_auth' && comment.patient_id === this.patientId)
        case 'encounter':
          return this.comments.filter(comment => comment.source !== 'insurance_auth' && comment.encounter_id === this.encounterId)
        default:
          console.log(`Invalid prop type: ${this.type}`)
          return []
      }
    },
    commentator(userId) {
      const user = this.$store.getters['users/getById'](userId)
      const name = `${user.first_name || ''} ${user.last_name || ''}`.trim()

      return name || userId
    },
    facilityTitle() {
      const facilityTitle = this.$store.getters['facilities/getById'](this.comment.encounter.place_of_service_id).title

      return facilityTitle || 'Facility not found.'
    },
    viewComment(item) {
      if (!this.loading) {
        // Set the comment value and open the comment modal
        const index = this.comments.findIndex(x => x.id === item.id)
        this.comment = this.comments[index]
        this.modalState = true

        // If the comment was unread and does not belong to the current user, change it to read
         if (!this.comment.is_read && this.comment.created_by_user_id !== this.userId) {
          this.commentSync(index, true)
        }
      }
    },
    commentStatusChange(item) {
      if (!item.id) {
        this.$store.dispatch('notify', { value: 'Comment read status cannot be changed for new comments', color: 'error' })
      } else if (!item.is_read) {
        this.$store.dispatch('notify', { value: 'Comments cannot be marked unread', color: 'error' })
      } else {
        const index = this.comments.findIndex(x => x.id === item.id)
        this.commentSync(index, item.is_read)
      }
    },

    // Handles updating comments is_read field
    commentSync(index, status) {
      if (this.online) {
        this.loading = true
        this.$store.dispatch('comments/syncComment', {
          id: this.comments[index].id,
          is_read: status,
          sync_log: this.syncLogSetup('I/A Comments Sync: Update Comments'),
        })
          .then(response => {
            if (response === true) {
              this.$store.commit('comments/updateCommentSync', {
                ...this.comments[index],
                is_read: status
              })
            } else {
              this.$store.dispatch('notify', { value: 'Comment read status update failed!', color: 'error' })
            }
          })
          .catch(() => {})
          .finally(() => {
            // Now download all comments again
            this.$store.dispatch('auth/refresh')
              .catch(error => {
                setTimeout(() => {
                  this.$store.dispatch('notify', { value: error , color: 'error' })
                }, 1000);
              })
            this.$store.dispatch('comments/loadComments')
              .catch(() => {})
              .finally(() => {
                this.loading = false
              })
          })
      } else {
        this.$nextTick(() => {
          this.$store.commit('comments/updateCommentSync', {
            ...this.comments[index],
            is_read: status,
            updated: true
          })

          this.$store.dispatch('notify', { value: 'Status will update on next sync. ', color: 'success' })

          this.$store.dispatch('encounters/setSynced', false)
        })
      }
    },

    // Handles post requests
    commentAdd() {
      this.$root.confirm({
        title: 'Save comment?',
        body: `${this.commentBody}`,
        cancel: 'Close',
        cancelColor: 'error',
        cancelIcon: this.icons.mdiClose,
        confirm: 'Send',
        confirmIcon: this.icons.mdiSend,
      }).then(result => {
        if (result === true) {
          if (this.online && this.isNoteSynced) {
            this.loading = true
            this.$store.dispatch('comments/postComment', this.newCommentTemplate)
              .then(response => {
                if (response.status === 200 || response.status === 'Success' || response === true) {
                  this.$store.dispatch('comments/loadComments')
                  this.$store.dispatch('notify', { value: 'Comment sent successfully. ', color: 'success' })
                  this.resetCommentBody()
                } else {
                  console.log('Error loading comments')
                }
              })
              .then(() => this.resetCommentBody())
              .catch(e => console.log(e))
          } else {
            this.$store.commit('comments/ADD_COMMENT', this.newCommentTemplate)

            this.$store.dispatch('notify',
            {
              value: 'Comment saved. Sync pending.',
              color: 'warning'
            })
            this.$store.dispatch('encounters/setSynced', false)
            this.resetCommentBody()
            this.loading = false
          }
        }
      })
    },
    viewPatient(patientId) {
      // Load the complete data for the related patient and encounters.
      this.$store.dispatch('patients/loadPatientData', patientId)
      this.$store.dispatch('encounters/loadEncounterData', patientId)

      this.$router.push({ name: 'view-patient', query: { id: patientId } })
    },
    viewEncounter(comment) {
      // Locally, the encounter is signed and synced, but the comment says the encounter is unsigned, so unsign
      if (this.commentEncounter.is_signed && this.commentEncounter.is_synced && !comment.encounter.is_signed) {
        this.commentEncounter.is_signed = false
        this.$store.commit('encounters/updateEncounter', { encounter: this.commentEncounter })
      }

      if (!comment?.encounter?.created_by_user_id) {
        const patientPlaceOfServiceId = this.$store.getters['patients/getById'](patientId).place_of_service_id
        const facilityNotCheckedOut = !!this.$store.getters['auth/getFacilityById'](patientPlaceOfServiceId)

        if (facilityNotCheckedOut) {
          // Encounter not found, probably needs to sync
          this.$store.dispatch('notify', {
            value: `Please add "${this.facilityTitle()}" to your list of facilities to view this encounter`, color: 'error', timeout: '10000',
          })
        } else {
          // Encounter not found, probably needs to sync
          this.$store.dispatch('notify', { value: 'Encounter not found, try syncing.', color: 'error' })
        }
      } else if (this.commentEncounter.is_signed && this.commentEncounter.is_synced) {
        this.$store.dispatch('patients/loadPatientData', this.commentEncounter.patient_id)
        this.$store.dispatch('encounters/loadEncounterData', this.commentEncounter.patient_id)

        // View signed surgical note
        this.$router.push({
          name: this.$store.getters['baseData/practiceTypeFromId'](this.commentEncounter.practice_type_id).noteRoute,
          query: { id: this.commentEncounter.id },
        })
      } else if (this.commentEncounter.created_by_user_id !== this.userId) {
        // Don't allow a different provider to edit an encounter
        this.$store.dispatch('notify', { value: 'You can\'t edit this encounter as you\'re not the provider who created it.', color: 'error' })
      } else {
        this.$store.dispatch('patients/loadPatientData', this.commentEncounter.patient_id)
        this.$store.dispatch('encounters/loadEncounterData', this.commentEncounter.patient_id)

        // Edit encounter note
        this.$store.dispatch('loading', true)
        this.$router.push({
          name: this.$store.getters['baseData/practiceTypeFromId'](this.commentEncounter.practice_type_id).encounterRoute,
          query: { id: this.commentEncounter.id },
        })
      }
    },
    rowClickHandler(item) {
      if (this.type === 'main') {
        this.viewComment(item)
      }
    },

    // Resets
    resetCommentBody() {
      this.commentBody = null
      this.loading = false
    },
    getPatientName() {
      if (this.comment?.patient_name)
        return this.comment.patient_name

      const patientId = this.patientId || this.commentEncounter.patient_id
      return  this.$store.getters['patients/getPatientNameById'](patientId)
    },
  },
}
</script>

<style lang="scss">
.v-data-table.comment-list .v-data-table__wrapper table tbody {
  tr {
    background-color: rgba(0, 0, 0, 0.08);
    td {
      padding: 0 0 !important;
      > * {
        display: inline-block;
        width: 100%;
        height: 100%;
        padding: 0 6px;
        padding-top: 13px;
      }
      .new {
        font-weight: 600;
        background-color: map-get($shades, 'white');
      }
    }
    &.v-data-table__empty-wrapper {
      background-color: map-get($shades, 'white');
    }
  }
}
.view-comment {
  .v-card__title {
    margin: 12px 0;
    text-align: left;
  }
  >.row {
    width: 100%;
    background-color: map-get($shades, 'white');
    border: thin solid $component-border-color;
    border-radius: 4px !important;
    padding: 20px !important;
    margin: 0 0 20px 0;
    pre {
      padding: 0 !important;
      font-family: "Inter", sans-serif;
      font-size: 18px !important;
      color: var(--v-secondary-darken4);
    }
  }
}
.v-card-title {
  display: block;
  width: 100%;

  .v-row {
    width: 100%;
    margin: 0;
  }
}
.is-read-cell {
  display: flex !important;
  justify-content: center;
  padding-left:1rem !important;
}
</style>
